export interface ClassificationReasoningChanges {
    category: boolean;
    class: boolean;
}

export const getReasoningKeysToStrip = (changes: ClassificationReasoningChanges): string[] => {
    const keys: string[] = [];
    if (changes.category) {
        keys.push('categoryReasoning', 'categoryRationale', 'categoryExplanation');
    }
    if (changes.class) {
        keys.push('classReasoning', 'classRationale', 'classExplanation');
    }
    return keys;
};

export const extractMillis = (value: unknown): number => {
    if (value && typeof value === 'object') {
        const maybeTimestamp = value as { toMillis?: () => number; seconds?: number };
        if (typeof maybeTimestamp.toMillis === 'function') {
            return maybeTimestamp.toMillis();
        }
        if (typeof maybeTimestamp.seconds === 'number') {
            return maybeTimestamp.seconds * 1000;
        }
    }
    return 0;
};

export const getReportSortMs = (data: Record<string, unknown>): number => {
    return Math.max(
        extractMillis(data.updatedAt),
        extractMillis(data.createdAt),
        extractMillis(data.generatedAt),
    );
};

export const stripReasoningFromContainer = (
    container: Record<string, unknown>,
    reasoningKeysToStrip: string[]
): Record<string, unknown> => {
    const lossClassification = container.lossClassification;
    if (!lossClassification || typeof lossClassification !== 'object') return container;

    const clonedLossClassification = { ...(lossClassification as Record<string, unknown>) };
    const data = clonedLossClassification.data;
    if (!data || typeof data !== 'object') {
        return { ...container, lossClassification: clonedLossClassification };
    }

    const clonedData = { ...(data as Record<string, unknown>) };
    reasoningKeysToStrip.forEach((key) => {
        delete clonedData[key];
    });
    clonedLossClassification.data = clonedData;

    return { ...container, lossClassification: clonedLossClassification };
};

export const stripReasoningFromDirectLossClassificationDoc = (
    docData: Record<string, unknown>,
    reasoningKeysToStrip: string[]
): Record<string, unknown> => {
    const type = docData.type;
    if (type !== 'lossClassification') return docData;

    const data = docData.data;
    if (!data || typeof data !== 'object') return docData;

    const clonedData = { ...(data as Record<string, unknown>) };
    reasoningKeysToStrip.forEach((key) => {
        delete clonedData[key];
    });

    return { ...docData, data: clonedData };
};

export const stripLossClassificationReasoning = (
    source: Record<string, unknown>,
    changes: ClassificationReasoningChanges
): Record<string, unknown> => {
    const reasoningKeysToStrip = getReasoningKeysToStrip(changes);
    if (reasoningKeysToStrip.length === 0) return source;

    let next = stripReasoningFromDirectLossClassificationDoc({ ...source }, reasoningKeysToStrip);
    next = stripReasoningFromContainer(next, reasoningKeysToStrip);
    const nestedAiReports = next.aiReports;
    if (nestedAiReports && typeof nestedAiReports === 'object') {
        next = {
            ...next,
            aiReports: stripReasoningFromContainer(nestedAiReports as Record<string, unknown>, reasoningKeysToStrip)
        };
    }
    return next;
};
